import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Heart, ArrowLeft, Sparkles, Quote } from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalendarPage = () => {
  const [date, setDate] = useState(new Date());
  const [affirmations, setAffirmations] = useState([]);
  const [selectedAffirmation, setSelectedAffirmation] = useState(null);
  const [loading, setLoading] = useState(true);

  const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  useEffect(() => {
    fetchAffirmations();
  }, []);

  useEffect(() => {
    if (affirmations.length > 0 && date) {
      // JavaScript getDay() returns 0 for Sunday, but our API uses 0 for Monday
      const jsDay = date.getDay();
      const dayOfWeek = jsDay === 0 ? 6 : jsDay - 1; // Convert to Monday=0 format
      const todaysAffirmations = affirmations.filter(a => a.day_of_week === dayOfWeek);
      setSelectedAffirmation(todaysAffirmations[0] || null);
    }
  }, [date, affirmations]);

  const fetchAffirmations = async () => {
    try {
      const response = await axios.get(`${API}/affirmations`);
      setAffirmations(response.data);
    } catch (error) {
      console.error("Error fetching affirmations:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get all affirmations for a specific day
  const getAffirmationsForDay = (dayOfWeek) => {
    return affirmations.filter(a => a.day_of_week === dayOfWeek);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      
      <section className="pt-28 pb-24 px-6 lg:px-12" data-testid="calendar-section">
        <div className="container mx-auto max-w-6xl">
          {/* Back Link */}
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-8">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>

          {/* Header */}
          <div className="mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-[#A69BE0] mb-4">
              Daily Healing
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#1A2A3A] mb-4">
              Affirmation Calendar
            </h1>
            <p className="text-lg text-[#1A2A3A]/60 max-w-2xl">
              Select a date to see your affirmation for the day. Each message is designed to support your healing journey.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Calendar */}
            <Card className="bg-white rounded-2xl shadow-card border border-[#E5E7EB]/50 overflow-hidden" data-testid="calendar-widget">
              <CardContent className="p-6">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(newDate) => newDate && setDate(newDate)}
                  className="rounded-xl"
                />
              </CardContent>
            </Card>

            {/* Selected Day Affirmation */}
            <div className="space-y-6">
              <Card className="affirmation-card shadow-soft" data-testid="selected-affirmation">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-3 font-heading text-xl text-[#1A2A3A]">
                    <Heart className="h-6 w-6 text-[#FFB7B2]" />
                    {date ? (
                      <span>
                        {dayNames[date.getDay() === 0 ? 6 : date.getDay() - 1]}'s Affirmation
                      </span>
                    ) : (
                      "Today's Affirmation"
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="animate-pulse">
                      <div className="h-8 bg-[#4FB3D4]/10 rounded w-3/4 mb-4"></div>
                      <div className="h-4 bg-[#4FB3D4]/10 rounded w-1/4"></div>
                    </div>
                  ) : selectedAffirmation ? (
                    <>
                      <p className="font-heading text-2xl md:text-3xl font-semibold text-[#1A2A3A] mb-4 leading-relaxed">
                        "{selectedAffirmation.text}"
                      </p>
                      <span className="inline-block px-4 py-2 rounded-full bg-[#4FB3D4]/10 text-[#4FB3D4] text-sm font-medium capitalize mb-6">
                        {selectedAffirmation.category}
                      </span>
                      
                      {/* Testimony */}
                      {selectedAffirmation.testimony && (
                        <div className="mt-6 p-4 bg-[#FDFBF7] rounded-xl border-l-4 border-[#A69BE0]">
                          <p className="text-sm font-medium text-[#A69BE0] mb-2">Real Story:</p>
                          <p className="text-[#1A2A3A]/70 text-sm italic">
                            "{selectedAffirmation.testimony}"
                          </p>
                        </div>
                      )}
                      
                      {/* Famous Quote */}
                      {selectedAffirmation.famous_quote && (
                        <div className="mt-4 p-4 bg-gradient-to-r from-[#4FB3D4]/5 to-[#DCD6F7]/10 rounded-xl">
                          <div className="flex items-start gap-3">
                            <Quote className="h-5 w-5 text-[#4FB3D4] flex-shrink-0 mt-1" />
                            <div>
                              <p className="text-[#1A2A3A]/80 text-sm italic mb-2">
                                "{selectedAffirmation.famous_quote.text}"
                              </p>
                              <p className="text-[#4FB3D4] text-xs font-medium">
                                — {selectedAffirmation.famous_quote.author}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-[#1A2A3A]/60">
                      No affirmation found for this day.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Tips Card */}
              <Card className="bg-gradient-to-br from-[#DCD6F7]/20 to-[#4FB3D4]/10 rounded-2xl border-0 p-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#A69BE0]/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-6 w-6 text-[#A69BE0]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A2A3A] mb-2">How to Use Affirmations</h3>
                    <ul className="text-[#1A2A3A]/60 space-y-1 text-sm">
                      <li>• Read your affirmation aloud each morning</li>
                      <li>• Write it in a journal for deeper reflection</li>
                      <li>• Repeat it whenever negative thoughts arise</li>
                      <li>• Believe in the words—you deserve healing</li>
                    </ul>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Weekly Overview */}
          <div className="mt-16">
            <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-6">
              Weekly Affirmations Overview
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {dayNames.map((day, index) => {
                const dayAffirmations = getAffirmationsForDay(index);
                const aff = dayAffirmations[0];
                return (
                  <Card 
                    key={day} 
                    className="bg-white rounded-xl shadow-card border border-[#E5E7EB]/50 p-4 hover:shadow-floating transition-all card-hover"
                    data-testid={`day-card-${day.toLowerCase()}`}
                  >
                    <h4 className="font-semibold text-[#1A2A3A] mb-2">{day}</h4>
                    {aff ? (
                      <>
                        <p className="text-sm text-[#1A2A3A]/60 line-clamp-2 mb-3">
                          "{aff.text}"
                        </p>
                        {aff.famous_quote && (
                          <p className="text-xs text-[#4FB3D4] italic">
                            — {aff.famous_quote.author}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-[#1A2A3A]/40">No affirmation set</p>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 bg-[#1A2A3A] text-white">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="font-heading text-2xl font-bold">ThryvLife</div>
            <nav className="flex gap-8">
              <Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link>
              <Link to="/recipes" className="text-white/70 hover:text-white transition-colors">Recipes</Link>
              <Link to="/resources" className="text-white/70 hover:text-white transition-colors">Resources</Link>
            </nav>
            <p className="text-white/50 text-sm">© 2025 ThryvLife. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CalendarPage;
